import {
  _Object,
  Coordinate,
  coordinates,
  Variant,
  _Element,
} from "@/lib/blaze/reality";

export const listObjectMissingElements = (
  obj: _Object,
): { a: Coordinate; b: Coordinate }[] => {
  const els = obj.elements;
  if (els.length === 0) return [];

  // 1) compute bounds
  const allA = els.map((e) => e.a);
  const allB = els.map((e) => e.b);
  const minA = Math.min(...allA) as Coordinate;
  const maxA = Math.max(...allA) as Coordinate;
  const minB = Math.min(...allB) as Coordinate;
  const maxB = Math.max(...allB) as Coordinate;

  // 2) restrict coordinate list to bounds
  const rangeA = coordinates.filter((c) => c >= minA && c <= maxA);
  const rangeB = coordinates.filter((c) => c >= minB && c <= maxB);

  // 3) mark existing (a,b) pairs
  const present = new Set(els.map((e) => `${e.a},${e.b}`));

  // 4) collect missing
  const missing: { a: Coordinate; b: Coordinate }[] = [];
  for (const a of rangeA) {
    for (const b of rangeB) {
      if (!present.has(`${a},${b}`)) missing.push({ a, b });
    }
  }

  return missing;
};

export const getGridFromObject = (obj: _Object) => {
  const missingElement = listObjectMissingElements(obj);
  if (missingElement.length > 0) {
    throw new Error("Object has missing elements");
  }
  const grid: number[][] = [];
  for (const element of obj.elements) {
    if (!grid[element.a]) {
      grid[element.a] = [];
    }
    grid[element.a][element.b] = parseInt(element.variant);
  }
  return grid;
};

export const getObjectFromGrid = (grid: number[][]) => {
  const elements: _Element[] = [];
  for (const [b, row] of grid.entries()) {
    for (const [a, value] of row.entries()) {
      elements.push(
        new _Element({
          a: a as Coordinate,
          b: b as Coordinate,
          variant: String(value) as Variant,
        }),
      );
    }
  }
  return new _Object(elements);
};

export const rebaseObjectCoordinates = (obj: _Object) => {
  // find min and max of a and b and bring min to 0
  const minA = Math.min(...obj.elements.map((e) => e.a));
  const minB = Math.min(...obj.elements.map((e) => e.b));
  const normalizedElements = obj.elements.map((e) => ({
    a: (e.a - minA) as Coordinate,
    b: (e.b - minB) as Coordinate,
    variant: e.variant,
  }));
  return new _Object(normalizedElements);
};

export const objectEquals = (obj1: _Object, obj2: _Object) => {
  const normalized1 = rebaseObjectCoordinates(obj1);
  const normalized2 = rebaseObjectCoordinates(obj2);
  // compare element by element
  for (let i = 0; i < normalized1.elements.length; i++) {
    if (normalized1.elements[i] !== normalized2.elements[i]) return false;
  }
  return true;
};

export const gridEquals = (grid1: number[][], grid2: number[][]) => {
  if (grid1.length !== grid2.length) return false;
  for (let i = 0; i < grid1.length; i++) {
    if (grid1[i].length !== grid2[i].length) return false;
    for (let j = 0; j < grid1[i].length; j++) {
      if (grid1[i][j] !== grid2[i][j]) return false;
    }
  }
  return true;
};

export const projectObject = (obj1: _Object, obj2: _Object): _Object => {
  const occ = new Set<string>(obj2.elements.map((e) => `${e.a},${e.b}`));
  const visibleFromObj1 = obj1.elements.filter(
    (e1) => !occ.has(`${e1.a},${e1.b}`),
  );
  const projected = [...obj2.elements, ...visibleFromObj1].map(
    (e) => new _Element(e),
  );
  return new _Object(projected);
};

export const collapseObjects = (objects: _Object[]): _Object => {
  if (objects.length === 0) {
    throw new Error("No objects to collapse");
  }
  return objects
    .slice(1)
    .reduce((acc, obj) => projectObject(acc, obj), objects[0]);
};

export const splitObjectByVariant = (
  obj: _Object,
  variant: Variant,
): _Object[] => {
  // TODO: implement
  return [];
};
export const fillObjectDefinedElements = (
  obj: _Object,
  variant: Variant,
): _Object => {
  // TODO: implement
  return obj;
};

export const fillObjectGapElements = (
  obj: _Object,
  variant: Variant,
): _Object => {
  // TODO: implement
  return obj;
};

export const fillObjectAllElements = (
  obj: _Object,
  variant: Variant,
): _Object => {
  return fillObjectDefinedElements(
    fillObjectGapElements(obj, variant),
    variant,
  );
};

export const rotateObject90 = (obj: _Object): _Object => {
  // TODO: implement
  return obj;
};

export const flipObjectHorizontally = (obj: _Object): _Object => {
  // TODO: implement
  return obj;
};

export const flipObjectVertically = (obj: _Object): _Object => {
  // TODO: implement
  return obj;
};
